/**
 * TrialExpiryBanner — Shows trial days remaining and a CTA to convert.
 * Distinct from TrialDemoBanner (which handles demo-mode awareness).
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ArrowRight, X, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTrialAccount } from "@/hooks/useTrialAccount";
import { differenceInDays, parseISO } from "date-fns";

export default function TrialExpiryBanner() {
  const navigate = useNavigate();
  const { trial, isTrialUser } = useTrialAccount();
  const [dismissed, setDismissed] = useState(false);

  const daysLeft = useMemo(() => {
    if (!trial?.expires_at) return null;
    const diff = differenceInDays(parseISO(trial.expires_at), new Date());
    return Math.max(0, diff);
  }, [trial?.expires_at]);

  if (!isTrialUser || dismissed || trial?.trial_status !== "active") return null;
  if (daysLeft === null) return null;

  const isUrgent = daysLeft <= 3;
  const isExpired = daysLeft === 0;

  return (
    <div
      className={`border-b px-4 py-2 ${
        isUrgent ? "bg-destructive/10 border-destructive/20" : "bg-primary/5 border-primary/10"
      }`}
    >
      <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
        <div className="flex items-center gap-2 min-w-0">
          {isUrgent ? (
            <AlertTriangle className="w-3.5 h-3.5 text-destructive shrink-0" />
          ) : (
            <Clock className="w-3.5 h-3.5 text-primary shrink-0" />
          )}
          <p className="text-xs text-muted-foreground truncate">
            <span className={`font-medium ${isUrgent ? "text-destructive" : "text-foreground"}`}>
              {isExpired ? "Trial expired" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your trial`}
            </span>
            {!isExpired && " — activate your account to keep your data and go live."}
            {isExpired && " — activate now to keep your data."}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant={isUrgent ? "destructive" : "default"}
            size="sm"
            className="h-7 text-xs gap-1"
            onClick={() => navigate("/trial/convert")}
          >
            Activate Account
            <ArrowRight className="w-3 h-3" />
          </Button>
          {!isExpired && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
              onClick={() => setDismissed(true)}
              aria-label="Dismiss trial banner"
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
